// Importaciones de React y React Router.
import React, { useState, useEffect, useRef } from 'react'; // Hooks de React para estado, efectos secundarios y referencias.
import { Link, useLocation } from 'react-router-dom'; // Componentes para navegación y acceso a la ruta actual.
import logo from '../logos/LOGOS DS CYBER-07.png'; // Logotipo de la empresa.

/**
 * Componente de la barra de navegación (Navbar).
 * Es adaptable (responsive) y cambia de apariencia al hacer scroll.
 * Incluye un menú de hamburguesa para dispositivos móviles y un submenú desplegable en escritorio.
 */
const Navbar = () => {
    // --- ESTADOS DEL COMPONENTE ---

    // `scrolled`: (booleano) Verdadero si el usuario ha hecho scroll hacia abajo más de 50px.
    const [scrolled, setScrolled] = useState(false);
    // `menuOpen`: (booleano) Controla la visibilidad del menú de hamburguesa en móviles.
    const [menuOpen, setMenuOpen] = useState(false);
    // `nosotrosSubMenuOpen`: (booleano) Controla la visibilidad del submenú "Nosotros" en escritorio.
    const [nosotrosSubMenuOpen, setNosotrosSubMenuOpen] = useState(false);
    // `serviciosSubMenuOpen`: (booleano) Controla la visibilidad del submenú "Servicios" en escritorio.
    const [serviciosSubMenuOpen, setServiciosSubMenuOpen] = useState(false);

    // --- HOOKS Y REFERENCIAS ---

    // `location`: Objeto que contiene información sobre la URL actual (ej. `location.pathname`).
    const location = useLocation();
    // `menuRef`: Referencia al contenedor del menú móvil para detectar clics fuera de él.
    const menuRef = useRef(null);
    // `subMenuRef`: Referencia al contenedor del submenú "Nosotros" para detectar clics fuera de él.
    const subMenuRef = useRef(null);
    // `serviciosSubMenuRef`: Referencia al contenedor del submenú "Servicios".
    const serviciosSubMenuRef = useRef(null);

    // --- EFECTOS SECUNDARIOS ---

    // Efecto que se ejecuta una vez al montar el componente y cada vez que `scrolled` cambia.
    useEffect(() => {
        // Función que se ejecuta al detectar el evento de scroll.
        const handleScroll = () => {
            const isScrolled = window.scrollY > 50; // Comprueba si el desplazamiento vertical es mayor a 50px.
            if (isScrolled !== scrolled) {
                setScrolled(isScrolled); // Actualiza el estado para cambiar el fondo del navbar.
            }
        };

        // Función para detectar clics fuera de los menús y cerrarlos.
        const handleClickOutside = (event) => {
            // Cierra el menú móvil si el clic fue fuera de su contenedor.
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuOpen(false);
            }

            // Cierra el submenú "Nosotros" si el clic fue fuera de su contenedor.
            if (subMenuRef.current && !subMenuRef.current.contains(event.target)) {
                setNosotrosSubMenuOpen(false);
            }

            // Cierra el submenú "Servicios" si el clic fue fuera de su contenedor.
            if (serviciosSubMenuRef.current && !serviciosSubMenuRef.current.contains(event.target)) {
                setServiciosSubMenuOpen(false);
            }
        };

        // Añade los listeners de eventos al montar el componente.
        window.addEventListener('scroll', handleScroll);
        document.addEventListener('mousedown', handleClickOutside);

        // Función de limpieza: se ejecuta al desmontar el componente para evitar fugas de memoria.
        return () => {
            window.removeEventListener('scroll', handleScroll);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [scrolled]); // La dependencia `scrolled` asegura que el efecto se re-evalue si este estado cambia.

    // --- LÓGICA DE RENDERIZADO ---

    // Determina el color de fondo del navbar. Transparente por defecto, semitransparente al hacer scroll.
    const bgColor = scrolled ? 'rgba(6, 28, 91, 0.85)' : 'transparent';

    // --- MANEJADORES DE EVENTOS ---

    // Alterna la visibilidad del menú móvil.
    const handleToggleMenu = () => {
        setMenuOpen(!menuOpen);
    };

    // Cierra todos los menús. Usado en los enlaces del menú móvil.
    const handleCloseMenu = () => {
        setMenuOpen(false);
        setNosotrosSubMenuOpen(false);
        setServiciosSubMenuOpen(false);
    };

    // Alterna la visibilidad del submenú "Nosotros" en la vista de escritorio.
    const handleToggleNosotrosSubMenu = (e) => {
        e.preventDefault();
        setNosotrosSubMenuOpen(!nosotrosSubMenuOpen);
        setServiciosSubMenuOpen(false);
    };

    // Alterna la visibilidad del submenú "Servicios" en la vista de escritorio.
    const handleToggleServiciosSubMenu = (e) => {
        e.preventDefault();
        setServiciosSubMenuOpen(!serviciosSubMenuOpen);
        setNosotrosSubMenuOpen(false);
    };

    return (
        <>
            {/* Div invisible que ocupa el espacio del navbar fijo para evitar que el contenido se solape al hacer scroll. */}
            <div style={{ height: scrolled ? '64px' : '0' }}></div>

            {/* Barra de navegación principal. Es fija en la parte superior. */}
            <nav className={`navbar text-white p-4 fixed w-full z-30 top-0 `} style={{ backgroundColor: bgColor }}>
                <div className="container mx-auto flex justify-between items-center">

                    {/* Sección del logo. */}
                    <div className="flex items-center">
                        <Link to="/" className="focus:outline-none">
                            <img src={logo} alt="Logo DS Cyber" className="h-16 mr-2 cursor-pointer" />
                        </Link>
                    </div>

                    {/* --- VISTA MÓVIL --- */}

                    {/* Botón de hamburguesa, visible solo en pantallas pequeñas (lg:hidden). */}
                    <div className="lg:hidden">
                        <button onClick={handleToggleMenu} className="text-white focus:outline-none">
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                            </svg>
                        </button>
                    </div>

                    {/* Menú desplegable para móviles. */}
                    <div
                        ref={menuRef} // Asigna la referencia para detectar clics fuera.
                        className={`lg:hidden fixed inset-x-0 top-20 p-4 z-50 bg-black bg-opacity-90 ${menuOpen ? 'block' : 'hidden'}`}
                        style={{ borderRadius: '0 0 10px 10px' }}
                    >
                        <div className="flex flex-col items-center py-4">
                            {/* Enlace "Nosotros" */}
                            <Link
                                to="/"
                                onClick={handleCloseMenu}
                                className="font-bold block w-full text-center py-3 text-lg text-white hover:text-gray-300 hover:bg-gray-800 transition-colors duration-300 rounded"
                                style={{ color: location.pathname === '/' ? '#3b82f6' : 'white' }} // Resalta si la ruta es activa.
                            >
                                Nosotros
                            </Link>

                            {/* Enlace "Servicios" */}
                            <Link
                                to="/servicios"
                                onClick={handleCloseMenu}
                                className="block text-center font-bold w-full px-4 py-4 text-lg text-white hover:text-gray-300 hover:bg-gray-800 transition-colors duration-300 rounded"
                                style={{ color: location.pathname === '/servicios' || location.pathname === '/recursos' ? '#3b82f6' : 'white' }}
                            >
                                Servicios
                            </Link>

                            {/* Sub-enlace Recursos (indentado bajo Servicios) */}
                            <Link
                                to="/recursos"
                                onClick={handleCloseMenu}
                                className="block text-center font-bold w-full px-4 py-2 text-base text-blue-300 hover:text-white hover:bg-gray-800 transition-colors duration-300 rounded"
                            >
                                └ Recursos
                            </Link>

                            {/* Enlace "Contacto" */}
                            <Link
                                to="/contacto"
                                onClick={handleCloseMenu}
                                className="block text-center font-bold w-full px-4 py-4 text-lg text-white hover:text-gray-300 hover:bg-gray-800 transition-colors duration-300 rounded"
                                style={{ color: location.pathname === '/contacto' ? '#3b82f6' : 'white' }}
                            >
                                Contacto
                            </Link>
                        </div>
                        {/* Botón para cerrar el menú móvil. */}
                        <button
                            onClick={handleCloseMenu}
                            className="text-white absolute top-4 right-4 focus:outline-none w-8 h-8 flex items-center justify-center"
                            aria-label="Cerrar menú"
                        >
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* --- VISTA DE ESCRITORIO --- */}

                    {/* Menú para pantallas grandes, oculto en móviles (hidden lg:flex). */}
                    <div className="hidden lg:flex items-center space-x-2 text-xl">

                        {/* Botón y submenú desplegable "Nosotros". */}
                        <div className="relative group" ref={subMenuRef}>
                            <button
                                onClick={handleToggleNosotrosSubMenu}
                                className="font-bold flex items-center text-xl py-2 px-3 min-w-[110px] text-center justify-center transition-colors duration-300 hover:text-gray-300"
                                style={{ color: location.pathname === '/' ? '#3b82f6' : 'white' }}
                            >
                                Nosotros
                                <svg
                                    className={`ml-1 w-4 h-4 transition-transform duration-200 ${nosotrosSubMenuOpen ? 'transform rotate-180' : ''}`}
                                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {/* Contenido del submenú. */}
                            {nosotrosSubMenuOpen && (
                                <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                                    <Link
                                        to="/"
                                        onClick={() => setNosotrosSubMenuOpen(false)}
                                        className="block w-full text-left px-4 py-2 text-sm text-gray-800 hover:bg-blue-500 hover:text-white"
                                    >
                                        Inicio
                                    </Link>
                                    {/* Este enlace especial hace scroll a la sección 'calendario' en la página de inicio. */}
                                    <Link
                                        to="/"
                                        onClick={() => {
                                            setNosotrosSubMenuOpen(false);
                                            // Si ya estamos en la home, hace scroll. Si no, navega y luego hará scroll (lógica en Home).
                                            if (location.pathname !== '/') {
                                                sessionStorage.setItem('scrollToCalendario', 'true');
                                            } else {
                                                document.getElementById('calendario')?.scrollIntoView({ behavior: 'smooth' });
                                            }
                                        }}
                                        className="block w-full text-left px-4 py-2 text-sm text-gray-800 hover:bg-blue-500 hover:text-white"
                                    >
                                        Calendario de Eventos
                                    </Link>
                                </div>
                            )}
                        </div>

                        {/* Botón desplegable "Servicios" */}
                        <div className="relative" ref={serviciosSubMenuRef}>
                            <button
                                onClick={handleToggleServiciosSubMenu}
                                className="font-bold flex items-center text-xl py-2 px-3 min-w-[110px] text-center justify-center transition-colors duration-300 hover:text-gray-300"
                                style={{ color: location.pathname === '/servicios' || location.pathname === '/recursos' ? '#3b82f6' : 'white' }}
                            >
                                Servicios
                                <svg
                                    className={`ml-1 w-4 h-4 transition-transform duration-200 ${serviciosSubMenuOpen ? 'transform rotate-180' : ''}`}
                                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {/* Submenú de Servicios */}
                            {serviciosSubMenuOpen && (
                                <div className="absolute left-0 mt-2 w-52 bg-white rounded-md shadow-lg py-1 z-50">
                                    <Link
                                        to="/servicios"
                                        onClick={() => setServiciosSubMenuOpen(false)}
                                        className="block w-full text-left px-4 py-2 text-sm text-gray-800 hover:bg-blue-500 hover:text-white"
                                    >
                                        Todos los servicios
                                    </Link>
                                    <Link
                                        to="/servicios#consultoria"
                                        onClick={() => setServiciosSubMenuOpen(false)}
                                        className="block w-full text-left px-4 py-2 text-sm text-gray-800 hover:bg-blue-500 hover:text-white"
                                    >
                                        Consultoría
                                    </Link>
                                    <Link
                                        to="/servicios#ciberseguridad"
                                        onClick={() => setServiciosSubMenuOpen(false)}
                                        className="block w-full text-left px-4 py-2 text-sm text-gray-800 hover:bg-blue-500 hover:text-white"
                                    >
                                        Ciberseguridad
                                    </Link>
                                    <Link
                                        to="/servicios#ia"
                                        onClick={() => setServiciosSubMenuOpen(false)}
                                        className="block w-full text-left px-4 py-2 text-sm text-gray-800 hover:bg-blue-500 hover:text-white"
                                    >
                                        Inteligencia Artificial
                                    </Link>
                                    <hr className="my-1 border-gray-200" />
                                    <Link
                                        to="/recursos"
                                        onClick={() => setServiciosSubMenuOpen(false)}
                                        className="block w-full text-left px-4 py-2 text-sm font-bold text-blue-700 hover:bg-blue-500 hover:text-white"
                                    >
                                        🎓 Recursos Gratuitos
                                    </Link>
                                </div>
                            )}
                        </div>

                        {/* Enlace "Contacto" */}
                        <Link
                            to="/contacto"
                            onClick={() => { setNosotrosSubMenuOpen(false); setServiciosSubMenuOpen(false); }}
                            className="font-bold text-xl py-2 px-3 min-w-[110px] text-center transition-colors duration-300 hover:text-gray-300"
                            style={{ color: location.pathname === '/contacto' ? '#3b82f6' : 'white' }}
                        >
                            Contacto
                        </Link>
                    </div>
                </div>
            </nav>
        </>
    );
};

// Exporta el componente para ser usado en otras partes de la aplicación.
export default Navbar;
